import {
  useMutation, useQuery, useInfiniteQuery, useQueryClient,
} from '@tanstack/react-query';
import {
  IPatchUserLicenseTypeParams, IPutProfileAvatarUpload, IUseGetUserById,
  IUsePatchCompleteUserParams, IUsePatchUpdateUserProfileParams, IPostPlatformRoleParams,
  IDeletePlatformRoleParams,
  IGetUsersParams,
  IPostAssignLicenseToIndependentUserParams,
  IUser,
  IPatchUserPlatformRolesParams,
  IPatchUpdateUserOrganizationParams,
  IUseGetUserLicenseByUserUuid,
} from '@/services/users/types';
import {
  getPreSignedProfileAvatarUploadUrl,
  getUserById,
  patchUserLicenseType,
  postPlatformRole,
  patchCompleteUser,
  patchUpdateUserProfile,
  putProfileAvatarUpload,
  deletePlatformRole,
  getUsers,
  patchUpdateUserOrganization,
  postAssignLicenseToIndependentUser,
  deleteRevokeLicenseToIndependentUser,
  postUserDeletionRequest,
  deleteUserDeleteByUserUuid,
  patchUserPlatformRolesByUserUuid,
  getUserLicenseByUserUuid,
} from '@/services/users/api';
import { QUERY_KEYS } from '@/consts/common';
import { invalidateUserRecord } from '@/utils/helpers/queryHelpers';

const useGetUserById = ({
  userUuid,
  fields,
  queryConfig = {},
}: IUseGetUserById) => useQuery({
  queryKey: [QUERY_KEYS.USER,
    userUuid,
    fields,
  ],
  queryFn: () => getUserById(userUuid, fields),
  ...queryConfig,
});

const usePatchCompleteUser = () => useMutation({
  mutationFn: ({
    userUuid,
    title,
    firstName,
    lastName,
    phone,
    countryCode,
  }: IUsePatchCompleteUserParams) => patchCompleteUser(userUuid, {
    title,
    firstName,
    lastName,
    phone,
    countryCode,
  }),
});

const useGetPreSignedUrlForProfileAvatarUpload = () => useQuery({
  queryKey: [QUERY_KEYS.PROFILE_AVATAR_PRESIGNED_URL],
  queryFn: () => getPreSignedProfileAvatarUploadUrl(),
  gcTime: 0, // to always get the latest presigned URL when fetching
  enabled: false,
});

const usePutProfileAvatarUpload = () => useMutation({
  mutationFn: ({
    binaryFileData,
    presignedUrl,
  }: IPutProfileAvatarUpload) => putProfileAvatarUpload({
    binaryFileData,
    presignedUrl,
  }),
});

const useGetUsers = ({
  limit = 10,
  sort = 'createdAt',
  order = 'desc',
  search = '',
  platformRole,
  licenseType,
  fields,
}: Omit<IGetUsersParams, 'page'>, queryEnabled: boolean = false) => useInfiniteQuery({
  initialPageParam: '',
  queryKey: [
    QUERY_KEYS.ALL_USER,
    limit,
    sort,
    order,
    search,
    platformRole,
    licenseType,
    fields?.join(',') ?? '',
  ],
  queryFn: ({ pageParam: cursorPageParam }: { pageParam: string }) => getUsers({
    cursor: cursorPageParam,
    limit,
    sort,
    order,
    search,
    platformRole,
    licenseType,
    fields,
  }),
  getNextPageParam: (lastPage) => lastPage.nextCursor,
  gcTime: 0,
  enabled: queryEnabled,
});

const usePatchUserLicenseType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userUuid,
      licenseType,
    }: IPatchUserLicenseTypeParams) => patchUserLicenseType(
      userUuid,
      { licensePrivilege: licenseType },
    ),
    onSuccess: (_data, { userUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_USER],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

const usePostPlatformRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userUuid,
      platformRole,
    }: IPostPlatformRoleParams) => postPlatformRole(
      userUuid,
      { platformPrivilege: platformRole },
    ),
    onSuccess: (_data, { userUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_USER],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

const useDeletePlatformRole = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userUuid,
      platformRole,
    }: IDeletePlatformRoleParams) => deletePlatformRole(
      userUuid,
      platformRole,
    ),
    onSuccess: (_data, { userUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_USER],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

const usePatchUpdateUserProfile = () => useMutation({
  mutationFn: ({
    userUuid,
    title = undefined,
    firstName = undefined,
    lastName = undefined,
    phone = undefined,
    countryCode = undefined,
  }: IUsePatchUpdateUserProfileParams) => patchUpdateUserProfile(userUuid, {
    title, firstName, lastName, phone, countryCode,
  }),
});

const usePatchUpdateUserOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      args: IPatchUpdateUserOrganizationParams,
    ) => patchUpdateUserOrganization(args),
    onSuccess: (_data, { userUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_USER],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_USERS],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

const useGetUserLicenseByUserUuid = ({
  userUuid,
  enabled = true,
}: IUseGetUserLicenseByUserUuid) => useQuery({
  queryKey: [QUERY_KEYS.LICENSE, userUuid],
  queryFn: () => getUserLicenseByUserUuid(userUuid),
  enabled: !!userUuid && enabled,
});

const usePostAssignLicenseToIndependentUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args:
    IPostAssignLicenseToIndependentUserParams) => postAssignLicenseToIndependentUser(args),
    onSuccess: (_data, { userUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_USER],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

const useDeleteRevokeLicenseToIndependentUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userUuid: IUser['userUuid']) => deleteRevokeLicenseToIndependentUser(userUuid),
    onSuccess: (_data, userUuid) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_USER],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

const usePostUserDeletionRequest = () => useMutation({
  mutationFn: (userUuid: IUser['userUuid']) => postUserDeletionRequest(userUuid),
});

const useDeleteUserDeleteByUserUuid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userUuid: IUser['userUuid']) => deleteUserDeleteByUserUuid(userUuid),
    onSuccess: (_data, userUuid) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_USER],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_USERS],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

const usePatchUserPlatformRolesByUserUuid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: IPatchUserPlatformRolesParams) => patchUserPlatformRolesByUserUuid(args),
    onSuccess: (_data, { userUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_USER],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_USERS],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

export {
  useGetUserById,
  usePatchCompleteUser,
  useGetPreSignedUrlForProfileAvatarUpload,
  usePutProfileAvatarUpload,
  useGetUsers,
  usePatchUserLicenseType,
  usePatchUpdateUserProfile,
  usePostPlatformRole,
  useDeletePlatformRole,
  usePatchUpdateUserOrganization,
  useGetUserLicenseByUserUuid,
  usePostAssignLicenseToIndependentUser,
  useDeleteRevokeLicenseToIndependentUser,
  usePostUserDeletionRequest,
  useDeleteUserDeleteByUserUuid,
  usePatchUserPlatformRolesByUserUuid,
};
